import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Copy, Eye, FileSpreadsheet, Layers, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/ui/StateBoundaries";
import { ConfirmDialog } from "@/components/admin/menu/shared";
import CustomReportTable from "@/components/reports/CustomReportTable";
import CustomReportRange, { presetRange } from "@/components/reports/CustomReportRange";
import {
  createCustomReport,
  deleteCustomReport,
  downloadBlobFile,
  exportPreviewBlob,
  failureText,
  getCatalogue,
  listClassificationProfiles,
  listCustomReports,
  previewCustomReport,
  simulateClassification,
  updateCustomReport,
} from "@/lib/customReportsApi";

/**
 * Settings → Reports → Custom reports (owner, 2026-10-24, v2.1):
 * Pick a data source, its columns in order, classification profiles, filters and sort; preview, then save.
 * Saved reports run from Reports → Custom reports.
 */

const OP_LABELS = {
  eq: "is", ne: "is not", in: "is one of", contains: "contains",
  gt: "more than", gte: "at least", lt: "less than", lte: "at most",
  empty: "is empty", not_empty: "is not empty",
};
const TIME_OP_LABELS = { gt: "after", gte: "on or after", lt: "before", lte: "on or before" };
const AGG_LABELS = {
  sum: "Total", avg: "Average", min: "Lowest", max: "Highest", count: "Count", count_distinct: "Distinct count",
};
const NO_VALUE = new Set(["empty", "not_empty"]);

export const outputKey = column => (column.agg ? `${column.agg}_${column.field}` : column.field);

const blank = dataset => ({
  dataset: dataset?.key || "",
  columns: [],
  filters: (dataset?.default_filters || []).map(f => ({ ...f })),
  sort: [],
});

const move = (list, index, by) => {
  const next = [...list];
  const target = index + by;
  if (target < 0 || target >= next.length) return list;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

export default function CustomReportBuilder() {
  const { token, settings, permissions } = usePOS();
  const dayStart = settings?.business_day_start || settings?.businessDayStart || "06:00";
  const canBuild = !Array.isArray(permissions) || permissions.includes("reports.custom");
  const canExport = !Array.isArray(permissions) || permissions.includes("reports.export");

  const [catalogue, setCatalogue] = useState(null);
  const [reports, setReports] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [isTemplate, setIsTemplate] = useState(false);
  const [definition, setDefinition] = useState(blank(null));
  const [range, setRange] = useState(() => ({ preset: "today", ...presetRange("today", dayStart) }));
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Classification Simulation Modal State
  const [simModalOpen, setSimModalOpen] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      getCatalogue(token),
      listCustomReports(token),
    ])
      .then(([cat, list]) => {
        if (!live) return;
        setCatalogue(cat);
        setReports(list);
        setDefinition(blank(cat.datasets[0]));
      })
      .catch(failure => live && setLoadError(failureText(failure, "Could not load the report builder")));
    return () => { live = false; };
  }, [token]);

  useEffect(() => {
    if (definition.dataset !== "bill_items" || !token) return;
    let live = true;
    if (typeof listClassificationProfiles === "function") {
      try {
        const p = listClassificationProfiles(token);
        if (p && typeof p.then === "function") {
          p.then(profs => live && setProfiles(profs || [])).catch(() => {});
        }
      } catch (_) {}
    }
    return () => { live = false; };
  }, [definition.dataset, token]);

  const dataset = useMemo(
    () => catalogue?.datasets.find(d => d.key === definition.dataset) || null,
    [catalogue, definition.dataset]
  );

  // Augment dataset fields with dynamic "classification" if profile selected
  const availableFieldsList = useMemo(() => {
    if (!dataset) return [];
    const fields = [...dataset.fields];
    if (dataset.key === "bill_items" && definition.classification_profile) {
      if (!fields.some(f => f.key === "classification")) {
        fields.unshift({
          key: "classification",
          label: "Classification",
          type: "text",
          domain: "classification",
        });
      }
    }
    return fields;
  }, [dataset, definition.classification_profile]);

  const fieldOf = useCallback(
    key => availableFieldsList.find(f => f.key === key) || dataset?.fields.find(f => f.key === key),
    [availableFieldsList, dataset]
  );

  const labelOf = column => {
    if (column.label) return column.label;
    const f = fieldOf(column.field);
    return column.agg ? `${AGG_LABELS[column.agg]} ${f?.label.toLowerCase()}` : f?.label;
  };

  const change = patch => {
    setDefinition(current => {
      const next = { ...current, ...patch };
      const keys = new Set(next.columns.map(outputKey));
      return { ...next, sort: next.sort.filter(s => keys.has(s.column)) };
    });
    setPreview(null);
    setPreviewError(null);
  };

  const startNew = () => {
    setEditingId(null);
    setName("");
    setIsTemplate(false);
    setDefinition(blank(catalogue?.datasets[0]));
    setPreview(null);
    setPreviewError(null);
  };

  const open = id => {
    if (!id) return startNew();
    const report = reports.find(r => r.id === Number(id));
    if (!report) return;
    setEditingId(report.id);
    setName(report.name);
    setIsTemplate(Boolean(report.is_template));
    setDefinition(report.definition);
    setPreview(null);
    setPreviewError(null);
  };

  const runPreview = async () => {
    setBusy(true);
    setPreviewError(null);
    try {
      setPreview(await previewCustomReport(definition, range, token));
    } catch (failure) {
      setPreview(null);
      setPreviewError(failureText(failure, "Could not run this report"));
    } finally {
      setBusy(false);
    }
  };

  const exportPreviewXlsx = async () => {
    setBusy(true);
    try {
      const res = await exportPreviewBlob(definition, "xlsx", range, token);
      downloadBlobFile(res, `restoyaar-${name || "report-preview"}.xlsx`);
      toast.success("Excel workbook downloaded");
    } catch (err) {
      toast.error(failureText(err, "Export failed"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setPreviewError(null);
    try {
      const saved = editingId
        ? (isTemplate ? await updateCustomReport(editingId, name, definition, token, isTemplate) : await updateCustomReport(editingId, name, definition, token))
        : (isTemplate ? await createCustomReport(name, definition, token, isTemplate) : await createCustomReport(name, definition, token));
      setReports(list => [...list.filter(r => r.id !== saved.id), saved].sort((a, b) => a.name.localeCompare(b.name)));
      setEditingId(saved.id);
      setIsTemplate(Boolean(saved.is_template));
      setDefinition(saved.definition);
      toast.success(`Saved "${saved.name}"`);
    } catch (failure) {
      setPreviewError(failureText(failure, "Could not save this report"));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setConfirmDelete(false);
    setBusy(true);
    try {
      await deleteCustomReport(editingId, token);
      setReports(list => list.filter(r => r.id !== editingId));
      toast.success(`Deleted "${name}"`);
      startNew();
    } catch (failure) {
      setPreviewError(failureText(failure, "Could not delete this report"));
    } finally {
      setBusy(false);
    }
  };

  const handleSimulateProfile = async () => {
    const slug = definition.classification_profile?.slug;
    const prof = profiles.find(p => p.slug === slug);
    if (!prof) return;
    setSimLoading(true);
    setSimResult(null);
    setSimModalOpen(true);
    try {
      const sim = await simulateClassification(prof.rules, prof.default_output, token);
      setSimResult(sim);
    } catch (err) {
      toast.error(failureText(err, "Simulation failed"));
    } finally {
      setSimLoading(false);
    }
  };

  if (loadError) return <p className="text-body text-destructive" role="alert" data-testid="custom-builder-load-error">{loadError}</p>;
  if (!catalogue) return <LoadingState message="Reading the report fields…" testId="custom-builder-loading" />;

  const usedPlain = new Set(definition.columns.filter(c => !c.agg).map(c => c.field));
  const outputColumns = definition.columns.map(c => ({ key: outputKey(c), label: labelOf(c) }));
  const summarised = definition.columns.some(c => c.agg);

  const setColumn = (index, patch) => change({ columns: definition.columns.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
  const setFilter = (index, patch) => change({ filters: definition.filters.map((f, i) => (i === index ? { ...f, ...patch } : f)) });
  const setSort = (index, patch) => change({ sort: definition.sort.map((s, i) => (i === index ? { ...s, ...patch } : s)) });

  const select = "rounded border bg-background px-2 py-1 text-body";
  const iconButton = "inline-flex h-7 w-7 items-center justify-center rounded border text-muted-foreground hover:text-foreground disabled:opacity-40";

  return (
    <section className="rounded-md border bg-card" data-testid="custom-report-builder">
      <header className="flex flex-wrap items-center justify-between gap-snug border-b px-3 py-2">
        <div>
          <h3 className="text-body font-bold">Custom reports</h3>
          <p className="text-micro text-muted-foreground">
            Choose the data, the columns in order, filters and sort. Saved reports appear under Reports → Custom reports.
          </p>
        </div>
        <div className="flex items-center gap-snug">
          <select value={editingId ?? ""} onChange={e => open(e.target.value)} className={select} aria-label="Saved report" data-testid="custom-builder-open">
            <option value="">New report</option>
            {reports.map(r => <option key={r.id} value={r.id}>{r.name} {r.is_template ? "(Template)" : ""}</option>)}
          </select>
          {editingId && canBuild && (
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)} disabled={busy} data-testid="custom-builder-delete">
              <Trash2 className="w-3.5 h-3.5 mr-tight" /> Delete
            </Button>
          )}
        </div>
      </header>

      <div className="space-y-base p-3">
        {!canBuild && (
          <p className="text-meta text-warning" data-testid="custom-builder-no-permission">
            You can look at these reports but not change them: building needs the "Build custom reports" permission.
          </p>
        )}

        <div className="grid gap-snug sm:grid-cols-2">
          <div className="flex flex-col gap-hair">
            <label className="text-meta font-medium">Report name</label>
            <Input value={name} onChange={e => setName(e.target.value)} maxLength={100} placeholder="Floor-wise takings" data-testid="custom-builder-name" />
            <label className="flex items-center gap-2 mt-1 text-meta text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={isTemplate}
                onChange={e => setIsTemplate(e.target.checked)}
                className="rounded border"
              />
              Save as reusable template
            </label>
          </div>
          <label className="flex flex-col gap-hair text-meta font-medium">
            Data
            <select
              value={definition.dataset}
              onChange={e => change(blank(catalogue.datasets.find(d => d.key === e.target.value)))}
              className={select}
              data-testid="custom-builder-dataset"
            >
              {catalogue.datasets.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
            <span className="text-micro text-muted-foreground font-normal">{dataset?.description}</span>
          </label>
        </div>

        {/* Classification Profile Picker (For Bill Items) */}
        {definition.dataset === "bill_items" && (
          <div className="p-3 bg-muted/30 rounded border flex flex-wrap items-center justify-between gap-3">
            <div>
              <h5 className="text-meta font-semibold flex items-center gap-1.5">
                <Layers className="w-4 h-4 text-primary" />
                Classification Profile
              </h5>
              <p className="text-micro text-muted-foreground">
                Group items into high-level business classifications (Food, Packed beverages, HS, Cig, etc.)
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={definition.classification_profile?.slug || ""}
                onChange={e => {
                  const slug = e.target.value;
                  if (!slug) {
                    const next = { ...definition };
                    delete next.classification_profile;
                    change(next);
                  } else {
                    const prof = profiles.find(p => p.slug === slug);
                    change({
                      classification_profile: { slug: prof.slug, version: prof.version },
                    });
                  }
                }}
                className={select}
              >
                <option value="">None (Raw menu items)</option>
                {profiles.map(p => (
                  <option key={p.slug} value={p.slug}>
                    {p.name} (v{p.version})
                  </option>
                ))}
              </select>

              {definition.classification_profile?.slug && (
                <Button variant="outline" size="sm" onClick={handleSimulateProfile} title="Simulate coverage and view rule priority">
                  <Eye className="w-3.5 h-3.5 mr-tight" /> Simulate Rules
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Columns, in the order they print. */}
        <div className="space-y-tight" data-testid="custom-builder-columns">
          <h4 className="text-meta font-semibold">Columns</h4>
          {summarised && (
            <p className="text-micro text-muted-foreground">
              Rows are grouped by every column shown as is; summarised columns add up within each group.
            </p>
          )}
          {definition.columns.map((column, index) => {
            const f = fieldOf(column.field);
            return (
              <div key={`${outputKey(column)}-${index}`} className="flex flex-wrap items-center gap-tight" data-testid={`custom-builder-column-${index}`}>
                <span className="min-w-[9rem] text-body font-medium">{f?.label || column.field}</span>
                <select
                  value={column.agg || ""}
                  onChange={e => setColumn(index, { agg: e.target.value || null })}
                  className={select}
                  aria-label={`How to show ${f?.label}`}
                  data-testid={`custom-builder-column-${index}-agg`}
                >
                  <option value="">Show value</option>
                  {(catalogue.aggs[f?.type || "text"] || []).map(a => <option key={a} value={a}>{AGG_LABELS[a]}</option>)}
                </select>
                <Input
                  value={column.label || ""}
                  onChange={e => setColumn(index, { label: e.target.value || null })}
                  placeholder={labelOf({ ...column, label: null })}
                  className="h-8 w-44"
                  aria-label={`Heading for ${f?.label}`}
                  data-testid={`custom-builder-column-${index}-label`}
                />
                <button type="button" className={iconButton} onClick={() => change({ columns: move(definition.columns, index, -1) })} disabled={index === 0} aria-label="Move left" data-testid={`custom-builder-column-${index}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" className={iconButton} onClick={() => change({ columns: move(definition.columns, index, 1) })} disabled={index === definition.columns.length - 1} aria-label="Move right" data-testid={`custom-builder-column-${index}-down`}><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" className={iconButton} onClick={() => change({ columns: definition.columns.filter((_, i) => i !== index) })} aria-label={`Remove ${f?.label}`} data-testid={`custom-builder-column-${index}-remove`}><X className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          <select
            value=""
            onChange={e => e.target.value && change({ columns: [...definition.columns, { field: e.target.value, agg: null, label: null }] })}
            className={select}
            aria-label="Add a column"
            data-testid="custom-builder-add-column"
          >
            <option value="">+ Add a column…</option>
            {availableFieldsList.map(f => (
              <option key={f.key} value={f.key} disabled={usedPlain.has(f.key)}>{f.label}</option>
            ))}
          </select>
        </div>

        {/* Filters. A new data source starts with the ones that keep it on settled bills. */}
        <div className="space-y-tight" data-testid="custom-builder-filters">
          <h4 className="text-meta font-semibold">Filters</h4>
          {definition.filters.map((filter, index) => {
            if (filter.logic) {
              return (
                <div key={index} className="p-2 border rounded bg-muted/20 flex items-center justify-between">
                  <span className="text-meta font-medium">Logical {filter.logic.toUpperCase()} Group ({filter.clauses?.length || 0} conditions)</span>
                  <button type="button" className={iconButton} onClick={() => change({ filters: definition.filters.filter((_, i) => i !== index) })}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              );
            }
            const f = fieldOf(filter.field);
            const type = f?.type || "text";
            const timeLike = type === "date" || type === "datetime";
            return (
              <div key={index} className="flex flex-wrap items-center gap-tight" data-testid={`custom-builder-filter-${index}`}>
                <select
                  value={filter.field}
                  onChange={e => {
                    const nf = fieldOf(e.target.value);
                    setFilter(index, { field: e.target.value, op: catalogue.ops[nf?.type]?.[0] || "eq", value: "" });
                  }}
                  className={select}
                  aria-label="Filter field"
                  data-testid={`custom-builder-filter-${index}-field`}
                >
                  {availableFieldsList.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
                <select
                  value={filter.op}
                  onChange={e => setFilter(index, { op: e.target.value })}
                  className={select}
                  aria-label="Condition"
                  data-testid={`custom-builder-filter-${index}-op`}
                >
                  {(catalogue.ops[type] || []).map(op => (
                    <option key={op} value={op}>{(timeLike && TIME_OP_LABELS[op]) || OP_LABELS[op]}</option>
                  ))}
                </select>
                {!NO_VALUE.has(filter.op) && (type === "bool" ? (
                  <select
                    value={String(filter.value ?? "")}
                    onChange={e => setFilter(index, { value: e.target.value === "" ? null : e.target.value === "true" })}
                    className={select}
                    aria-label="Value"
                    data-testid={`custom-builder-filter-${index}-value`}
                  >
                    <option value="">Choose…</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <Input
                    type={type === "date" ? "date" : type === "datetime" ? "datetime-local" : type === "text" ? "text" : "number"}
                    value={Array.isArray(filter.value) ? filter.value.join(", ") : filter.value ?? ""}
                    onChange={e => setFilter(index, { value: e.target.value })}
                    placeholder={filter.op === "in" ? "comma, separated" : ""}
                    className="h-8 w-48"
                    aria-label="Value"
                    data-testid={`custom-builder-filter-${index}-value`}
                  />
                ))}
                <button type="button" className={iconButton} onClick={() => change({ filters: definition.filters.filter((_, i) => i !== index) })} aria-label="Remove filter" data-testid={`custom-builder-filter-${index}-remove`}><X className="w-3.5 h-3.5" /></button>
              </div>
            );
          })}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const first = availableFieldsList[0];
              change({ filters: [...definition.filters, { field: first.key, op: catalogue.ops[first.type][0], value: "" }] });
            }}
            disabled={!dataset}
            data-testid="custom-builder-add-filter"
          >
            <Plus className="w-3.5 h-3.5 mr-tight" /> Add filter
          </Button>
        </div>

        {/* Sort, first rule first. */}
        <div className="space-y-tight" data-testid="custom-builder-sort">
          <h4 className="text-meta font-semibold">Sort</h4>
          {definition.sort.map((rule, index) => (
            <div key={index} className="flex flex-wrap items-center gap-tight" data-testid={`custom-builder-sort-${index}`}>
              <span className="text-meta text-muted-foreground w-12">{index === 0 ? "By" : "Then"}</span>
              <select value={rule.column} onChange={e => setSort(index, { column: e.target.value })} className={select} aria-label="Sort column" data-testid={`custom-builder-sort-${index}-column`}>
                {outputColumns.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <select value={rule.dir} onChange={e => setSort(index, { dir: e.target.value })} className={select} aria-label="Direction" data-testid={`custom-builder-sort-${index}-dir`}>
                <option value="desc">Latest / highest / Z first</option>
                <option value="asc">Oldest / lowest / A first</option>
              </select>
              <button type="button" className={iconButton} onClick={() => change({ sort: move(definition.sort, index, -1) })} disabled={index === 0} aria-label="Move up" data-testid={`custom-builder-sort-${index}-up`}><ArrowUp className="w-3.5 h-3.5" /></button>
              <button type="button" className={iconButton} onClick={() => change({ sort: definition.sort.filter((_, i) => i !== index) })} aria-label="Remove sort" data-testid={`custom-builder-sort-${index}-remove`}><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const used = new Set(definition.sort.map(s => s.column));
              const next = outputColumns.find(c => !used.has(c.key));
              if (next) change({ sort: [...definition.sort, { column: next.key, dir: "desc" }] });
            }}
            disabled={definition.sort.length >= outputColumns.length}
            data-testid="custom-builder-add-sort"
          >
            <Plus className="w-3.5 h-3.5 mr-tight" /> Add sort
          </Button>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-snug border-t pt-base">
          {dataset?.date_range ? (
            <CustomReportRange value={range} onChange={next => { setRange(next); setPreview(null); }} dayStart={dayStart} testId="custom-builder-range" />
          ) : (
            <span className="text-meta text-muted-foreground">{dataset?.label} is not limited by dates.</span>
          )}
          <div className="flex items-center gap-snug">
            <Button variant="outline" onClick={runPreview} disabled={busy || !definition.columns.length} data-testid="custom-builder-preview">
              Preview
            </Button>
            {canBuild && (
              <Button variant="action" onClick={save} disabled={busy || !definition.columns.length || !name.trim()} data-testid="custom-builder-save">
                <Save className="w-3.5 h-3.5 mr-tight" /> {editingId ? "Save changes" : "Save report"}
              </Button>
            )}
          </div>
        </div>

        {previewError && <p className="text-body text-destructive" role="alert" data-testid="custom-builder-error">{previewError}</p>}
        {preview && (
          <CustomReportTable
            result={preview}
            canExport={canExport}
            filename={`restoyaar-${name || "custom-report"}`}
            testId="custom-builder-preview-table"
            onExportXlsx={exportPreviewXlsx}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title={`Delete "${name}"?`}
        body="The report disappears from Reports for everyone. The bills it read are not touched."
        confirmLabel="Delete report"
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
        testId="custom-builder-delete-dialog"
      />

      {/* Classification Simulation Modal */}
      {simModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border shadow-xl max-w-2xl w-full max-h-[85vh] flex flex-col p-4 animate-in fade-in-50">
            <div className="flex items-center justify-between border-b pb-3 mb-3">
              <div>
                <h4 className="text-body font-bold flex items-center gap-2">
                  <Layers className="w-4 h-4 text-primary" />
                  Classification Simulation Report
                </h4>
                <p className="text-micro text-muted-foreground">
                  Evaluated against catalog and recent sold items
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSimModalOpen(false)}
                className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {simLoading && <LoadingState message="Testing rules against live menu catalog..." />}

            {simResult && (
              <div className="space-y-3 overflow-y-auto pr-1 text-meta">
                <div className="grid grid-cols-3 gap-2 p-2 bg-muted/40 rounded text-center">
                  <div>
                    <div className="text-body font-bold">{simResult.total_candidates_evaluated}</div>
                    <div className="text-micro text-muted-foreground">Total Menu Items</div>
                  </div>
                  <div>
                    <div className="text-body font-bold text-emerald-600">
                      {(100 - simResult.unmatched_percentage).toFixed(1)}%
                    </div>
                    <div className="text-micro text-muted-foreground">Categorized</div>
                  </div>
                  <div>
                    <div className="text-body font-bold text-amber-600">
                      {simResult.unmatched_count} ({simResult.unmatched_percentage}%)
                    </div>
                    <div className="text-micro text-muted-foreground">Fallback to "{simResult.default_output}"</div>
                  </div>
                </div>

                {simResult.dead_rules?.length > 0 && (
                  <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-destructive text-meta">
                    <strong>Rule Conflict Warning:</strong>
                    <ul className="list-disc pl-5 mt-1 text-micro">
                      {simResult.dead_rules.map((d, i) => (
                        <li key={i}>
                          Rule #{d.priority} ("{d.output}") matches 0 items ({d.reason}).
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <h5 className="font-semibold text-meta mt-2">Rule Coverage & Priority Hierarchy:</h5>
                <div className="border rounded divide-y divide-border">
                  {simResult.rules.map(r => (
                    <div key={r.priority} className="p-2 flex items-center justify-between text-meta">
                      <div>
                        <span className="font-mono text-micro text-muted-foreground mr-2">#{r.priority}</span>
                        <span className="font-semibold">{r.output}</span>
                        {r.sample_items?.length > 0 && (
                          <div className="text-micro text-muted-foreground">
                            e.g. {r.sample_items.join(", ")}
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <span className="font-medium tabular-nums">{r.matched_count} items</span>
                        <span className="text-micro text-muted-foreground ml-1">({r.percentage}%)</span>
                      </div>
                    </div>
                  ))}
                </div>

                {simResult.sample_unmatched?.length > 0 && (
                  <div>
                    <h5 className="font-semibold text-meta">Unmatched Items (Falling into "{simResult.default_output}"):</h5>
                    <p className="text-micro text-muted-foreground font-mono mt-1 p-2 bg-muted rounded">
                      {simResult.sample_unmatched.join(", ")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
